import AzureSpeechSettingsView from '@UI/Store/AiConfiguration/Views/AzureSpeechSettingsView';
import type {
	AiModelPayload,
	AiUsageValuePayload,
	LabeledEnumPayload,
} from '@Application/Ai/AiConfiguration/Models/Actions/aiModelsFetched';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `usages` stays a plain array of raw enum values, never `{ value, label }`: server-side it is a bare
 * array column, with no enum serializer applied to it — the screen resolves labels from
 * `aiUsagesStore`. `lastTestedUsage`, `family` and `deliveryMode` are real nullable enum columns and always come back
 * as `{ value, label }` or `null`. `providerOptions` is read back like any other configuration field,
 * so the edit form can show what is registered rather than ask for it again.
 */
class AiModelView {
	readonly id: number;
	readonly identifier: string;
	readonly label: string;
	readonly usages: readonly string[];
	readonly lastTestedAt: string | null;
	readonly lastTestSucceeded: boolean | null;
	readonly lastTestMessage: string | null;
	readonly lastTestedUsage: AiUsageValuePayload | null;
	/** `null` means "no override": the provider's own `apiVersion` applies. */
	readonly apiVersion: string | null;
	readonly family: LabeledEnumPayload | null;
	readonly deliveryMode: LabeledEnumPayload | null;
	/** French labels of the settings the last connection test rejected; `null` until a test has run. */
	readonly lastTestRejectedSettings: readonly string[] | null;
	readonly providerOptions: Record<string, unknown> | null;
	readonly transcriptionChunking: boolean;
	/** `null` means "the server's own value applies". */
	readonly transcriptionChunkMaxSeconds: number | null;
	/** `null` means "the server's own value applies". */
	readonly transcriptionConcurrency: number | null;
	/** Euro prices, `null` until set. */
	readonly inputPricePerMillionTokens: number | null;
	readonly outputPricePerMillionTokens: number | null;
	readonly cachedInputPricePerMillionTokens: number | null;
	readonly cacheWritePricePerMillionTokens: number | null;
	readonly pricePerAudioMinute: number | null;
	readonly billedPerStartedMinute: boolean;
	/** `null` outside the Azure Speech family. */
	readonly azureSpeechSettings: AzureSpeechSettingsView | null;
	readonly providerId: number;
	readonly accountId: number;

	constructor(payload: AiModelPayload) {
		this.id = payload.id;
		this.identifier = payload.identifier;
		this.label = payload.label;
		this.usages = payload.usages;
		this.lastTestedAt = payload.lastTestedAt;
		this.lastTestSucceeded = payload.lastTestSucceeded;
		this.lastTestMessage = payload.lastTestMessage;
		this.lastTestedUsage = payload.lastTestedUsage;
		this.apiVersion = payload.apiVersion;
		this.family = payload.family;
		this.deliveryMode = payload.deliveryMode;
		this.lastTestRejectedSettings = payload.lastTestRejectedSettings;
		this.providerOptions = payload.providerOptions;
		this.transcriptionChunking = payload.transcriptionChunking;
		this.transcriptionChunkMaxSeconds = payload.transcriptionChunkMaxSeconds;
		this.transcriptionConcurrency = payload.transcriptionConcurrency;
		this.inputPricePerMillionTokens = payload.inputPricePerMillionTokens;
		this.outputPricePerMillionTokens = payload.outputPricePerMillionTokens;
		this.cachedInputPricePerMillionTokens = payload.cachedInputPricePerMillionTokens;
		this.cacheWritePricePerMillionTokens = payload.cacheWritePricePerMillionTokens;
		this.pricePerAudioMinute = payload.pricePerAudioMinute;
		this.billedPerStartedMinute = payload.billedPerStartedMinute;
		this.azureSpeechSettings =
			payload.azureSpeechSettings === null
				? null
				: new AzureSpeechSettingsView(payload.azureSpeechSettings);
		this.providerId = payload.provider.id;
		this.accountId = payload.account.id;

		Object.freeze(this);
	}

	get hasBeenTested(): boolean {
		return this.lastTestSucceeded !== null;
	}

	/** At least one price is set (0 included); without any, calls get a cost that is not set. */
	get hasPrices(): boolean {
		return (
			this.inputPricePerMillionTokens !== null ||
			this.outputPricePerMillionTokens !== null ||
			this.cachedInputPricePerMillionTokens !== null ||
			this.cacheWritePricePerMillionTokens !== null ||
			this.pricePerAudioMinute !== null
		);
	}

	/** Applies a fresh connection test result, leaving every other field untouched. */
	withConnectionTestResult(
		succeeded: boolean,
		testedAt: string,
		message: string,
		usage: AiUsageValuePayload,
		rejectedSettings: string[],
	): AiModelView {
		return new AiModelView({
			id: this.id,
			identifier: this.identifier,
			label: this.label,
			usages: [...this.usages],
			lastTestedAt: testedAt,
			lastTestSucceeded: succeeded,
			lastTestMessage: message,
			lastTestedUsage: usage,
			apiVersion: this.apiVersion,
			family: this.family,
			deliveryMode: this.deliveryMode,
			lastTestRejectedSettings: rejectedSettings,
			providerOptions: this.providerOptions,
			transcriptionChunking: this.transcriptionChunking,
			transcriptionChunkMaxSeconds: this.transcriptionChunkMaxSeconds,
			transcriptionConcurrency: this.transcriptionConcurrency,
			inputPricePerMillionTokens: this.inputPricePerMillionTokens,
			outputPricePerMillionTokens: this.outputPricePerMillionTokens,
			cachedInputPricePerMillionTokens: this.cachedInputPricePerMillionTokens,
			cacheWritePricePerMillionTokens: this.cacheWritePricePerMillionTokens,
			pricePerAudioMinute: this.pricePerAudioMinute,
			billedPerStartedMinute: this.billedPerStartedMinute,
			azureSpeechSettings:
				this.azureSpeechSettings === null
					? null
					: {
							...this.azureSpeechSettings,
							phrases: [...this.azureSpeechSettings.phrases],
						},
			provider: { id: this.providerId },
			account: { id: this.accountId },
		});
	}
}

export default AiModelView;
